/** The games this browser has created or hosted, newest first. Per browser, not per account. */

const KEY = 'pbt:myGames';
/** Enough for a season of trivia nights; keeps the home page from turning into a log. */
const LIMIT = 12;

export type MyGameRole = 'created' | 'hosted';

export interface MyGame {
  code: string;
  name: string;
  role: MyGameRole;
  /** ms epoch of the last time this browser opened it — drives the ordering. */
  seenAt: number;
}

function isMyGame(value: unknown): value is MyGame {
  const g = value as MyGame | null;
  return Boolean(g && typeof g.code === 'string' && g.code);
}

export function loadMyGames(): MyGame[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isMyGame)
      .map<MyGame>((g) => ({
        code: g.code.toUpperCase(),
        name: g.name || g.code.toUpperCase(),
        role: g.role === 'created' ? 'created' : 'hosted',
        seenAt: Number(g.seenAt) || 0,
      }))
      .sort((a, b) => b.seenAt - a.seenAt);
  } catch {
    return [];
  }
}

function write(games: MyGame[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(games.slice(0, LIMIT)));
  } catch {
    // Private browsing with storage disabled: the list is simply always empty.
  }
}

/**
 * Record a game as mine. Called on create, and again every time this browser
 * opens the host screen — "created" outranks "hosted" and is never downgraded.
 */
export function rememberGame(game: { code: string; name: string; role: MyGameRole }): MyGame[] {
  const code = game.code.toUpperCase();
  const existing = loadMyGames().find((g) => g.code === code);
  const next: MyGame = {
    code,
    name: game.name || code,
    role: existing?.role === 'created' ? 'created' : game.role,
    seenAt: Date.now(),
  };
  const games = [next, ...loadMyGames().filter((g) => g.code !== code)];
  write(games);
  return games.slice(0, LIMIT);
}

export function forgetGame(code: string): MyGame[] {
  const games = loadMyGames().filter((g) => g.code !== code.toUpperCase());
  write(games);
  return games;
}
